/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/
import { useEffect, useState } from "react";

// @mui material components
import Divider from "@mui/material/Divider";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import PrimaryButton from "../../../../components/PrimaryButton";
import FormField from "components/FormField";
import { useAgencyInfo } from "context/agency";
import StripeLogo from "assets/images/Stripe-Emblem.png";
import { useAppServices } from "hook/services";
import SoftAvatar from "components/SoftAvatar";
import Success from "../Success";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import toast from "react-hot-toast";
import { Warning } from "@mui/icons-material";
import env from "config";
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

function Accounts({ data }) {
  const [agency, Update] = useAgencyInfo();
  const AppService = useAppServices();
  const [slack2FA, setSlack2FA] = useState(true);
  const [spotify2FA, setSpotify2FA] = useState(true);
  const [atlassian2FA, setAtlassian2FA] = useState(true);
  const [asana2FA, setAsana2FA] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [googleApiKeyValidation, setGoogleApiKeyValidation] = useState(null);
  const [googleProcessing, setGoogleProcessing] = useState(false);
  const [stripeName, setStripeName] = useState("");
  const [apiKey, setApiKey] = useState(data?.apiKey || "");
  const hasApiKey = Boolean(apiKey || data?.has_api_key);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [openWhitelabelModal, setopenWhitelabelModal] = useState(false);
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(agency?.recaptcha_enabled || false);
  const SERVER_IP = env.SERVER_IP;

  const handleSetSlack2FA = () => setSlack2FA(!slack2FA);
  const handleSetSpotify2FA = () => setSpotify2FA(!spotify2FA);
  const handleSetAtlassian2FA = () => setAtlassian2FA(!atlassian2FA);
  const handleSetAsana2FA = () => setAsana2FA(!asana2FA);
  const handleSave = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setValidationError(null);

    const payload = {
      _id: agency._id,
      external_key: e.target.external_key.value,
    };

    const { response } = await AppService.agency.ValidateApiKey({
      payload,
    });

    if (response?.success) {
      Update(response.data);
      setValidationError({ success: true, message: "Your API key is valid!" });
      toast.success("Your API key is valid!");
    } else {
      setValidationError({ success: false, message: "Invalid API key. Please try again." });
    }

    if (response) {
      Update(response.data);
      setProcessing(false);
    } else {
      setProcessing(false);
    }
  };
  const handleSaas = async (e) => {
    setProcessing(true);
    const { response } = await AppService.saas.subscriptions({
      query: `agency_id=${agency?._id}`,
      payload: true,
    });
    if (response) {
      setProcessing(false);
    } else {
      setProcessing(false);
    }
  };

  const [open, setOpen] = useState(false);
  const [stripeModel, setStripeModel] = useState(false);

  // Handlers to open and close the modal
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleOpenStripeModel = () => {
    setStripeModel(true);
  };
  // Function to handle the delete action
  const handleDelete = () => {
    console.log("Delete action triggered");
    setOpen(false);
  };

  const deletefromagency = async () => {
    setProcessing(true);
    const { response } = await AppService.saas.deletestripfromagency({
      query: `agency_id=${agency?._id}`,
      payload: true,
    });
    if (response) {
      handleClose();
      setProcessing(false);
    } else {
      handleClose();
      setProcessing(false);
    }
  };

  const handleSaveGoogleApiKey = async (e) => {
    e.preventDefault();
    setGoogleProcessing(true);
    setGoogleApiKeyValidation(null);

    const payload = { _id: agency._id };
    const formData = new FormData(e.target);

    const googleApiKey = (
      formData.get("google_api_key") ??
      e.target.google_api_key?.value ??
      ""
    )
      .toString()
      .trim();
    const googleImageApiKey = (
      formData.get("google_image_api_key") ??
      e.target.google_image_api_key?.value ??
      ""
    )
      .toString()
      .trim();

    payload.google_api_key = googleApiKey || null;
    payload.google_image_api_key = googleImageApiKey || null;

    const { response } = await AppService.agency.update({
      payload,
    });

    if (response?.success) {
      Update(response.data);
      const removedWebsiteKey = !googleApiKey;
      const successMessage = removedWebsiteKey
        ? "Google Maps API removed."
        : "Your Google API key is saved!";
      setGoogleApiKeyValidation({ success: true, message: successMessage });
      toast.success(successMessage);
    } else {
      setGoogleApiKeyValidation({
        success: false,
        message: response?.message || "Failed to save Google API key. Please try again.",
      });
    }

    setGoogleProcessing(false);
  };

  const handleSaveRecaptchaSettings = async (e) => {
    e.preventDefault();
    setGoogleProcessing(true);

    const payload = { _id: agency._id };
    const formData = new FormData(e.target);

    payload.recaptcha_enabled = recaptchaEnabled;
    const recaptchaSiteKey = (
      formData.get("recaptcha_site_key") ||
      e.target.recaptcha_site_key?.value ||
      ""
    ).trim();
    const recaptchaSecretKey = (
      formData.get("recaptcha_secret_key") ||
      e.target.recaptcha_secret_key?.value ||
      ""
    ).trim();

    payload.recaptcha_site_key = recaptchaSiteKey || null;
    payload.recaptcha_secret_key = recaptchaSecretKey || null;

    if (recaptchaEnabled && (!recaptchaSiteKey || !recaptchaSecretKey)) {
      payload.recaptcha_enabled = false;
      setRecaptchaEnabled(false);
    }

    const { response } = await AppService.agency.update({
      payload,
    });

    if (response?.success) {
      Update(response.data);
      setRecaptchaEnabled(response.data?.recaptcha_enabled || false);
      toast.success("reCAPTCHA settings saved!");
    } else {
      toast.error(response?.message || "Failed to save reCAPTCHA settings.");
    }

    setGoogleProcessing(false);
  };

  const GetStripeaccountname = async () => {
    const { response } = await AppService.agency.GetStripeAccountName({
      query: `agency_id=${agency?._id}`,
    });
    if (response) {
      setStripeName(response?.data?.account_name);
    }
  };

  useEffect(() => {
    GetStripeaccountname();
    setRecaptchaEnabled(agency?.recaptcha_enabled || false);
  }, [agency?.recaptcha_enabled]);

  const [value, setValue] = useState(
    agency?.measurementId
      ? new URLSearchParams(new URL(agency.measurementId).search).get("id") || ""
      : ""
  );
  const [error, setError] = useState("");

  const validateAndFormat = (input) => {
    let formatted = input.trim().toUpperCase();
    formatted = formatted.replace(/[^A-Z0-9-]/g, "");
    if (!formatted.startsWith("G-")) {
      formatted = "G-" + formatted;
    }
    return formatted;
  };

  const handleChange = (e) => {
    const input = e.target.value;
    const formatted = validateAndFormat(input);
    setValue(formatted);

    const idOnly = formatted.replace("G-", "");
    if (idOnly.length < 6 || idOnly.length > 12) {
      setError("Measurement ID must be 6–12 characters long");
    } else {
      setError("");
    }
  };

  const handleSaveMeasurment = async () => {
    if (!error && value) {
      const payload = {
        _id: agency._id,
        measurementId: `https://www.googletagmanager.com/gtag/js?id=${value}`,
      };
      const { response } = await AppService.agency.update({
        payload,
      });

      if (response?.success) {
        Update(response?.data);
        toast.success("Google Analytics Measurement ID updated successfully");
      }
    }
  };

  const generateApiKey = async () => {
    setLoading(true);
    setApiKey("");
    setCopied(false);

    // Simulate backend delay (replace with actual API call)
    await new Promise((resolve) => setTimeout(resolve, 1500));

    const length = 40;
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let newKey = "api_";
    for (let i = 0; i < length; i++) {
      newKey += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    const payload = {
      _id: agency._id,
      apiKey: newKey,
    };

    const { response } = await AppService.agency.update({
      payload,
    });
    if (response?.success) {
      Update(response?.data);
      setApiKey(newKey);
      toast.success("API Key generated and saved successfully");
    } else {
      toast.error(response?.message || "Could not generate the API key");
    }

    setLoading(false);
  };

  const handleCopy = async () => {
    if (!apiKey) return;

    try {
      // Try the standard clipboard API first
      await navigator.clipboard.writeText(apiKey);
    } catch (err) {
      // Fallback: create a temporary textarea
      const textArea = document.createElement("textarea");
      textArea.value = apiKey;
      textArea.style.position = "fixed";
      textArea.style.top = "-1000px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isValid = !error && value.length > 0;

  return (
    <>
      <div
        className="equalheight d-flex w-100 "
        style={{
          border: "1px solid rgba(229, 229, 229, 1)",
          background: "white",
          borderRadius: "5px",
          justifyContent: "space-between",
        }}
      >
        <div className="d-flex p-20-px text-black text-bold text-left w-50-per">
          <SoftAvatar src={StripeLogo} alt="Slack logo" variant="rounded" />
          <SoftBox ml={2} lineHeight={0}>
            <SoftTypography variant="h5" fontWeight="medium">
              Stripe Integration
            </SoftTypography>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              Integrate your Stripe Account
            </SoftTypography>
            <SoftTypography variant="h6" display="flex" gap="10px" fontWeight="medium">
              Account Name:{" "}
              <p
                style={{
                  fontSize: "14px",
                  fontFamily: "sans-serif",
                  fontWeight: "medium",
                  color: "grey",
                }}
              >
                {stripeName}
              </p>
            </SoftTypography>
          </SoftBox>
        </div>
        <div className="p-20-px text-black text-bold text-right ml-auto ">
          {/* {agency?.stripe && (
            <Success
              action={handleSaas}
              text1={"Your Request Has Been Submitted!"}
              text2={"It Will Take A little Time!"}
            />
          )} */}
          {data?.stripe ? (
            <button
              className="btn-new-design-black-addacount font-16"
              style={{
                margin: "0px 0px 0px 10px",
                padding: "10px 20px",
                border: "green",
                color: "green",
              }}
              type="submit"
              variant="outlined"
              color="success"
              size="small"
              onClick={handleOpenStripeModel}
            >
              Connected
            </button>
          ) : (
            <button
              className="btn-new-design-black-addacount font-16"
              style={{
                margin: "0px 0px 0px 10px",
                padding: "10px 20px",
                border: "green",
                color: "green",
              }}
              disabled={processing}
              loading={processing}
              type="submit"
              variant="outlined"
              color="dark"
              size="small"
            >
              <a
                style={{ color: "inherit" }}
                rel="noreferrer"
                target="_blank"
                href={`https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${env.STRIPE_CONNECT_CLIENT_ID}&scope=read_write&redirect_uri=${encodeURIComponent(env.STRIPE_CONNECT_REDIRECT_URI)}&state=${agency._id}`}
                disabled={processing}
                loading={processing}
              >
                Connect
              </a>
            </button>
          )}
          {data.stripe && (
            <button
              onClick={handleOpen}
              className="btn-new-design-black-addacount font-16"
              style={{
                margin: "0px 0px 0px 10px",
                padding: "10px 20px",
                border: "red",
                color: "red",
              }}
              type="submit"
              variant="outlined"
              color={agency?.stripe ? "dark" : "success"}
              size="small"
            >
              {data.stripe && "Disconnect"}
            </button>
          )}
          {/* Modal Component */}
          {open && (
            <div className="modal-zz-confirm text-left">
              <div
                className="scrollable-modal-content bg-white rounded"
                style={{ position: "relative" }}
              >
                <h2 className="text-black text-left font-14"> Confirm Action </h2>
                <span className="crossison" onClick={handleClose}>
                  X
                </span>
                <p>Are you sure you want to disconnect?</p>
                <div style={{ display: "flex", justifyContent: "space-around", marginTop: "20px" }}>
                  <Button variant="dark" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button variant="dark" color="red" onClick={deletefromagency}>
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
          {stripeModel && (
            <div className="modal-zz-confirm text-left">
              <div
                className="scrollable-modal-content bg-white rounded"
                style={{ position: "relative" }}
              >
                <h2 className="text-left font-14" style={{ color: "red" }}>
                  <Warning /> Warning{" "}
                </h2>
                <span className="crossison" onClick={() => setStripeModel(false)}>
                  X
                </span>
                <div
                  style={{
                    marginTop: "15px",
                    color: "#d32f2f",
                    fontSize: "18px",
                    fontWeight: "500",
                  }}
                >
                  Connecting a different Stripe account may disrupt existing user subscriptions and
                  payment data. Proceed with caution.
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "20px" }}>
                  <button
                    onClick={() => setStripeModel(false)}
                    className="btn-new-design-black-addacount font-16"
                    style={{
                      margin: "0px 0px 0px 10px",
                      padding: "10px 20px",
                      border: "black",
                      color: "black",
                    }}
                    type="submit"
                    variant="outlined"
                    color="dark"
                    size="small"
                  >
                    Cancel
                  </button>
                  <button
                    className="btn-new-design-black-addacount font-16"
                    style={{
                      margin: "0px 0px 0px 10px",
                      padding: "10px 20px",
                      border: "green",
                      color: "green",
                    }}
                    disabled={processing}
                    loading={processing}
                    type="submit"
                    variant="outlined"
                    color="success"
                    size="small"
                  >
                    <a
                      style={{ color: "inherit" }}
                      rel="noreferrer"
                      target="_blank"
                      href={`https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${env.STRIPE_CONNECT_CLIENT_ID}&scope=read_write&redirect_uri=${encodeURIComponent(env.STRIPE_CONNECT_REDIRECT_URI)}&state=${agency._id}`}
                      disabled={processing}
                      loading={processing}
                    >
                      Connected
                    </a>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* <SoftBox
        mt={2}
        width="98%"
        display="flex"
        gap={2}
        alignItems="center"
        component="form"
        justifyContent="space-between"
        onSubmit={handleSave}
      >
        <div style={{ width: "50%" }}>
          <FormField
            label="Enter External Authentication Key"
            name="external_key"
            defaultValue={agency?.external_key}
            width="50%"
          />

          {validationError && (
            <p
              className={`mt-2 text-sm font-semibold ${
                validationError.success ? "text-green-600" : "text-red-600"
              }`}
              style={{
                color: `${validationError.success ? "green" : "red"}`,
                fontSize: "14px",
              }}
            >
              {validationError.message}
            </p>
          )}
        </div>

        <SoftBox>
          <SoftButton
            sx={{ mt: 2 }}
            disabled={processing}
            loading={processing}
            type="submit"
            variant="outlined"
            color="dark"
            size="small"
          >
            Save
          </SoftButton>
        </SoftBox>
      </SoftBox> */}

      <SoftBox
        mt={2}
        width="98%"
        display="flex"
        gap={2}
        alignItems="center"
        component="form"
        justifyContent="space-between"
        onSubmit={handleSaveGoogleApiKey} // Separate submit handler
        key={`google-api-form-${agency?._id}-${agency?.google_api_key ?? ""}-${agency?.google_image_api_key ?? ""}`}
        autoComplete="off"
      >
        <div style={{ width: "50%" }}>
          <div style={{ width: "100%", marginBottom: "15px" }}>
            <FormField
              label="Enter Google Maps API Key - Website Restricted"
              name="google_api_key"
              defaultValue={agency?.google_api_key || ""}
              width="100%"
              inputProps={{
                autoComplete: "off",
                autoCorrect: "off",
                autoCapitalize: "off",
                spellCheck: "false",
              }}
            />
          </div>
          <FormField
            label="Enter Google Maps API Key - IP Restricted"
            name="google_image_api_key"
            defaultValue={agency?.google_image_api_key}
            width="50%"
          />
          {googleApiKeyValidation && (
            <p
              className={`mt-2 text-sm font-semibold ${
                googleApiKeyValidation.success ? "text-green-600" : "text-red-600"
              }`}
              style={{
                color: `${googleApiKeyValidation.success ? "green" : "red"}`,
                fontSize: "14px",
              }}
            >
              {googleApiKeyValidation.message}
            </p>
          )}
          <button
            style={{ marginTop: "10px" }}
            className="btn-new-design-black"
            type="button"
            onClick={() => setopenWhitelabelModal(true)}
          >
            How to restrict API key?
          </button>
        </div>

        <SoftBox>
          <PrimaryButton type="submit" disabled={googleProcessing} style={{ marginTop: 16 }}>
            {googleProcessing ? "Saving..." : "Save Google API Key"}
          </PrimaryButton>
        </SoftBox>
      </SoftBox>

      {/* reCAPTCHA Settings */}
      <SoftBox
        mt={4}
        width="98%"
        display="flex"
        flexDirection="column"
        gap={2}
        component="form"
        onSubmit={handleSaveRecaptchaSettings}
      >
        <SoftTypography variant="h6" fontWeight="bold">
          reCAPTCHA Protection
        </SoftTypography>
        
        <FormControlLabel
          control={
            <Switch
              checked={recaptchaEnabled}
              onChange={async (e) => {
                const newValue = e.target.checked;
                setRecaptchaEnabled(newValue);
                
                // Automatically save toggle state
                const payload = {
                  _id: agency._id,
                  recaptcha_enabled: newValue,
                };
                
                try {
                  const { response } = await AppService.agency.update({
                    payload,
                  });
                  
                  if (response?.success) {
                    Update(response.data);
                    toast.success("reCAPTCHA setting updated!");
                  }
                } catch (error) {
                  console.error("Failed to update reCAPTCHA setting:", error);
                  // Revert on error
                  setRecaptchaEnabled(!newValue);
                  toast.error("Failed to update reCAPTCHA setting");
                }
              }}
            />
          }
          label="Enable reCAPTCHA Protection"
          sx={{ marginLeft: 0 }}
        />

        {recaptchaEnabled && (
          <div style={{ width: "50%", marginTop: "10px" }}>
            <div style={{ width: "100%", marginBottom: "15px" }}>
              <FormField
                label="Enter Google reCAPTCHA Site Key"
                name="recaptcha_site_key"
                defaultValue={agency?.recaptcha_site_key}
                width="100%"
                placeholder="6Le..."
              />
            </div>
            <div style={{ width: "100%" }}>
              <FormField
                label="Enter Google reCAPTCHA Secret Key"
                name="recaptcha_secret_key"
                defaultValue={agency?.recaptcha_secret_key}
                width="100%"
                placeholder="6Le..."
              />
            </div>
            <p style={{ fontSize: "12px", color: "#666", marginTop: "10px", marginBottom: "0" }}>
              Get your reCAPTCHA keys from{" "}
              <a
                href="https://www.google.com/recaptcha/admin/create"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1976d2" }}
              >
                Google reCAPTCHA Admin
              </a>
              . Site Key is public, Secret Key is private (for backend validation).
            </p>
          </div>
        )}

        {recaptchaEnabled && (
          <SoftBox>
            <PrimaryButton type="submit" disabled={googleProcessing} style={{ marginTop: 16 }}>
              {googleProcessing ? "Saving..." : "Save reCAPTCHA Settings"}
            </PrimaryButton>
          </SoftBox>
        )}
      </SoftBox>

      <Card sx={{ maxWidth: 500, mt: 4, boxShadow: 3 }}>
        <CardHeader
          title="Google Analytics"
          subheader="Configure your Google Analytics Measurement ID to start tracking website analytics."
          sx={{ textAlign: "center" }}
        />

        <CardContent>
          <TextField
            fullWidth
            id="ga-id"
            label="Measurement ID"
            value={value}
            onChange={handleChange}
            placeholder="Enter your GA Measurement ID"
            error={!!error}
            helperText={error || "Example: G-ABC12345 (6–12 characters after G-)"}
          />

          <Box mt={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {isValid && <Alert severity="success">Valid Measurement ID format</Alert>}
          </Box>

          <SoftButton
            onClick={handleSaveMeasurment}
            disabled={!isValid}
            variant="outlined"
            color={isValid ? "dark" : "secondary"}
            fullWidth
            sx={{ mt: 2 }}
          >
            {isValid ? "Save Configuration" : "Enter Valid ID"}
          </SoftButton>
        </CardContent>
      </Card>
      <Card sx={{ maxWidth: 700, mt: 4, boxShadow: 3 }}>
        <CardHeader title="AI Conversations API Key" sx={{ textAlign: "center" }} />
        <CardContent>
          <Button
            variant="contained"
            color="primary"
            onClick={generateApiKey}
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? (
              <>
                <CircularProgress size={22} color="inherit" sx={{ mr: 1 }} />
                Generating...
              </>
            ) : hasApiKey ? (
              "Regenerate API Key"
            ) : (
              "Generate API Key"
            )}
          </Button>

          {!apiKey && (
            <Box mt={3}>
              <Alert severity={hasApiKey ? "success" : "info"}>
                {hasApiKey
                  ? "An API key is active for this agency. It is only shown once, right after it is generated, so generate a new one if you no longer have it — the previous key stops working."
                  : "No API key has been generated yet. Generate one to connect AI Conversations."}
              </Alert>
            </Box>
          )}

          {apiKey && (
            <Box mt={3} display="flex" alignItems="center" gap={1}>
              <Alert
                severity="info"
                sx={{
                  flexGrow: 1,
                  wordBreak: "break-all",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                {apiKey}
              </Alert>

              <Tooltip title={copied ? "Copied!" : "Copy"}>
                <IconButton onClick={handleCopy} color={copied ? "success" : "default"}>
                  {copied ? <CheckIcon /> : <ContentCopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </CardContent>
      </Card>
      {openWhitelabelModal && (
        <div className="modal-zz-confirm">
          <div
            className="scrollable-modal-content bg-white rounded"
            style={{ border: "1px solid black", position: "relative",maxHeight:"600px",overflowY:"auto" }}
          >
            <h2 className="text-center font-14">Restrict Google API Key</h2>

            <span className="crossison" onClick={() => setopenWhitelabelModal(false)}>
              X
            </span>

            <div className="list-model0">
              <p>
              To secure your Google API Key, follow the steps below carefully:
              </p>

              <ul>
                <li className="listtext">
                  <b>1.</b> Go to <b>Google Cloud Console</b> → <b>APIs & Services</b> →
                  <b> Credentials</b>.
                </li>

                <li className="listtext">
                  <b>2.</b> Create a new <b>API Key</b> or click an existing key you want to secure.
                </li>
              </ul>

              <h4 className="font-14 mt-2">Restrict API Key with Website (Frontend)</h4>
              <ul>
                <li className="listtext">
                  <b>3.</b> Under <b>Application restrictions</b>, select
                  <b> HTTP referrers (websites)</b>.
                </li>

                <li className="listtext">
                  <b>4.</b> Add these allowed domains..
                  <br />
                  <div className="text-red-500">
                  <code>{agency?.domain ? `https://${agency?.domain}` : "https://preview.easyaidirectory.com"}</code>
                  <br />
                  <code>{env.ACCOUNT_LEVEL_URL}</code>
                  </div>
                </li>
              </ul>

              <h4 className="font-14 mt-2">Restrict API Key with IP Address (Backend)</h4>
              <ul>
                <li className="listtext">
                  <b>6.</b> Under <b>Application restrictions</b>, select
                  <b> IP addresses</b>.
                </li>

                <li className="listtext">
                  <b>7.</b> Add your server public IP address
                  <br />
                  <div className="text-red-500">
                  <code>{SERVER_IP}</code>
                  </div>
                </li>
              </ul>

              <h4 className="font-14 mt-2">Restrict API Usage (Recommended)</h4>
              <ul>
                <li className="listtext">
                  <b>9.</b> Under <b>API restrictions</b>, choose <b>Restrict key</b>.
                </li>

                <li className="listtext">
                  <b>10.</b> Select only required APIs such as:
                  <br />
                  Maps JavaScript API, Places API, Geocoding API.
                </li>
              </ul>

              <p className="mt-2">
                <b>Note:</b> Always use <b>separate API keys</b> for frontend (website restricted)
                and backend (IP restricted). Changes may take <b>5–10 minutes</b> to apply.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Accounts;
